import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateInvoiceInput,
  Invoice,
  PatchInvoiceInput,
  State,
  InvoiceBulkImportObject
} from '@thro-types/billing';

import { AxiosRequestConfig } from 'axios';

import {
  cancelInvoice,
  createInvoice,
  deleteInvoice,
  getInvoice,
  getInvoices,
  getMyInvoices,
  getMyInvoicesCounts,
  getPdfFilePathForPreview,
  importInvoicesBulk,
  importInvoicesBulkFromDB,
  updateInvoice,
  updateInvoiceState
} from 'api/billingService';

import { PDFGeneratorData } from './pdfTypes';

export const useGetInvoiceQuery = (id: string, config?: AxiosRequestConfig) => {
  const queryKey = ['invoices', id, config ? JSON.stringify(config.params) : ''];
  return useQuery({
    queryKey,
    queryFn: () => {
      return getInvoice(id, config);
    }
  });
};

export const useGetInvoicesListQuery = (config?: AxiosRequestConfig) => {
  const queryKey = ['invoices', config ? JSON.stringify(config.params) : ''];
  return useQuery({
    queryKey,
    queryFn: () => {
      return getInvoices(config);
    },
    placeholderData: keepPreviousData
  });
};

export const useGetMyInvoicesListQuery = (config?: AxiosRequestConfig) => {
  const queryKey = ['invoices', config ? JSON.stringify(config.params) : ''];
  return useQuery({
    queryKey,
    queryFn: () => {
      return getMyInvoices(config);
    },
    placeholderData: keepPreviousData
  });
};

export const useGetMyInvoicesCountsQuery = (config?: AxiosRequestConfig) => {
  const queryKey = ['invoices', 'counts', config ? JSON.stringify(config.params) : ''];
  return useQuery({
    queryKey,
    queryFn: () => {
      return getMyInvoicesCounts(config);
    },
    placeholderData: keepPreviousData
  });
};

export const useInvoiceMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();

  const queryKey = ['invoices'];

  const creation = useMutation<Invoice, Error, CreateInvoiceInput>({
    mutationFn: async args => {
      return await createInvoice(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });
  const update = useMutation<Invoice, Error, { id: string; data: PatchInvoiceInput }>({
    mutationFn: async args => {
      return await updateInvoice(args.id, args.data, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });
  const updateState = useMutation<
    Invoice[],
    Error,
    { state: keyof typeof State; invoiceIds: string[] }
  >({
    mutationFn: async args => {
      return await updateInvoiceState(args.state, args.invoiceIds, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });
  const cancel = useMutation<Invoice, Error, Invoice['id']>({
    mutationFn: async args => {
      return await cancelInvoice(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });
  const deletion = useMutation<Invoice, Error, Invoice['id']>({
    mutationFn: async args => {
      return await deleteInvoice(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  return { creation, update, updateState, cancel, deletion } as const;
};

export const useBulkInvoicesImportMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();
  const queryKey = ['invoices', config ? JSON.stringify(config.params) : ''];
  const create = useMutation<InvoiceBulkImportObject, Error, FormData>({
    mutationFn: async args => {
      return await importInvoicesBulk(args, config);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: () => {}
  });
  return { create } as const;
};

export const useBulkInvoicesImportFromDBMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();
  const queryKey = ['invoices', config ? JSON.stringify(config.params) : ''];
  const create = useMutation<string[], Error, { id: string }>({
    mutationFn: async args => {
      return await importInvoicesBulkFromDB(args.id, config);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: () => {}
  });
  return { create } as const;
};

export const usePdfCreateInvoiceMutation = (config?: AxiosRequestConfig) => {
  const create = useMutation<Blob, Error, PDFGeneratorData>({
    mutationFn: async args => {
      return await getPdfFilePathForPreview(args, config);
    },
    onSuccess: () => {},
    onError: () => {}
  });
  return { create } as const;
};
